import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Package, Plus, Loader2, X, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { ModuleGate } from "@/components/PlanNotIncluded";

type Item = {
    id: string; sku: string | null; name: string; category: string | null;
    unit: string; min_level: number; is_equipment: boolean; is_active: boolean;
    notes: string | null; stock_total: number; stock_by_branch: Record<string, number>;
    below_min_level: boolean;
};

export default function InventoryItems() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const canCreate = hasPerm("inventory.create");
    const [rows, setRows] = useState<Item[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [onlyBelow, setOnlyBelow] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ sku: "", name: "", category: "consumable", unit: "ədəd", min_level: 0, is_equipment: false, notes: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const r = await api.get(`/inventory/items${onlyBelow ? "?only_below_min=true" : ""}`);
            setRows(r.data.items || []);
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyBelow]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true); setErr(null);
        try {
            await api.post("/inventory/items", {
                sku: form.sku.trim() || null,
                name: form.name.trim(),
                category: form.category || null,
                unit: form.unit || "ədəd",
                min_level: Number(form.min_level) || 0,
                is_equipment: form.is_equipment,
                notes: form.notes.trim() || null,
            });
            setCreating(false);
            setForm({ sku: "", name: "", category: "consumable", unit: "ədəd", min_level: 0, is_equipment: false, notes: "" });
            await load();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setSaving(false); }
    };

    return (
        <ModuleGate module="inventory">
            <div className="space-y-4" data-testid="inv-items-page">
                <header className="flex items-center gap-3 flex-wrap">
                    <Package size={22} style={{ color: "var(--primary)" }} />
                    <h1 className="text-xl font-semibold flex-1" style={{ color: "var(--text)" }}>{t("inventory.items.title")}</h1>
                    <label className="inline-flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
                        <input type="checkbox" data-testid="inv-items-filter-below" checked={onlyBelow} onChange={(e) => setOnlyBelow(e.target.checked)} />
                        {t("inventory.items.filter.below_min")}
                    </label>
                    {canCreate && (
                        <button data-testid="inv-item-new-btn" onClick={() => setCreating(true)}
                            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium"
                            style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                            <Plus size={16} /> {t("inventory.item.new")}
                        </button>
                    )}
                </header>
                {err && <div data-testid="inv-items-err" className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
                {loading && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}
                {!loading && !rows.length && <div className="surface-card p-6 rounded-xl text-sm" style={{ color: "var(--text-muted)", background: "var(--card)", border: "1px solid var(--border)" }} data-testid="inv-items-empty">{t("inventory.items.empty")}</div>}
                {!!rows.length && (
                    <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <table className="w-full text-sm">
                            <thead>
                                <tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    <th className="text-left px-3 py-2">{t("inventory.item.sku")}</th>
                                    <th className="text-left px-3 py-2">{t("inventory.item.name")}</th>
                                    <th className="text-left px-3 py-2">{t("inventory.item.category")}</th>
                                    <th className="text-right px-3 py-2">{t("inventory.item.stock_total")}</th>
                                    <th className="text-right px-3 py-2">{t("inventory.item.min_level")}</th>
                                    <th className="text-left px-3 py-2">{t("common.status")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((it) => (
                                    <tr key={it.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`inv-item-row-${it.id}`}>
                                        <td className="px-3 py-2 font-mono text-xs" style={{ color: "var(--text-muted)" }}>{it.sku || "—"}</td>
                                        <td className="px-3 py-2" style={{ color: "var(--text)" }}>
                                            {it.name}
                                            {it.is_equipment && <span className="ml-2 text-xs rounded-full px-2 py-0.5" style={{ background: "var(--info-bg)", color: "var(--info-fg)" }}>{t("inventory.item.equipment")}</span>}
                                        </td>
                                        <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{it.category || "—"}</td>
                                        <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--text)" }}>{it.stock_total} <span className="text-xs" style={{ color: "var(--text-muted)" }}>{it.unit}</span></td>
                                        <td className="px-3 py-2 text-right" style={{ color: "var(--text-muted)" }}>{it.min_level}</td>
                                        <td className="px-3 py-2">
                                            {it.below_min_level ? (
                                                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid={`inv-item-below-${it.id}`}>
                                                    <AlertTriangle size={10} /> {t("inventory.item.below_min")}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs" style={{ background: "var(--success-bg)", color: "var(--success-fg)" }}>
                                                    <span aria-hidden="true">●</span>
                                                    <span>{t("inventory.item.ok")}</span>
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {creating && (
                    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }} onClick={() => setCreating(false)}>
                        <div className="w-full max-w-lg rounded-xl border p-5" style={{ background: "var(--card)", borderColor: "var(--border)" }} onClick={(e) => e.stopPropagation()} data-testid="inv-item-modal">
                            <div className="flex items-center gap-2 mb-4">
                                <h2 className="text-lg font-semibold flex-1" style={{ color: "var(--text)" }}>{t("inventory.item.new")}</h2>
                                <button aria-label="close" onClick={() => setCreating(false)}><X size={18} style={{ color: "var(--text-muted)" }} /></button>
                            </div>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                        <span>{t("inventory.item.sku")}</span>
                                        <input data-testid="inv-item-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}
                                            className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                    </label>
                                    <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                        <span>{t("inventory.item.name")} *</span>
                                        <input required data-testid="inv-item-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                    </label>
                                    <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                        <span>{t("inventory.item.category")}</span>
                                        <select data-testid="inv-item-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                                            className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }}>
                                            <option value="consumable">{t("inventory.category.consumable")}</option>
                                            <option value="equipment">{t("inventory.category.equipment")}</option>
                                            <option value="other">{t("inventory.category.other")}</option>
                                        </select>
                                    </label>
                                    <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                        <span>{t("inventory.item.unit")}</span>
                                        <input data-testid="inv-item-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                                            className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                    </label>
                                    <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                        <span>{t("inventory.item.min_level")}</span>
                                        <input type="number" min={0} step="0.01" data-testid="inv-item-minlevel" value={form.min_level} onChange={(e) => setForm({ ...form, min_level: parseFloat(e.target.value || "0") })}
                                            className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                    </label>
                                    <label className="flex items-center gap-2 text-xs mt-6" style={{ color: "var(--text-muted)" }}>
                                        <input type="checkbox" data-testid="inv-item-isequip" checked={form.is_equipment} onChange={(e) => setForm({ ...form, is_equipment: e.target.checked })} />
                                        {t("inventory.item.is_equipment")}
                                    </label>
                                </div>
                                <div className="flex justify-end gap-2 pt-2">
                                    <button type="button" onClick={() => setCreating(false)} className="rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text-muted)", minHeight: 40 }}>{t("common.cancel")}</button>
                                    <button type="submit" disabled={saving} data-testid="inv-item-save" className="rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                                        {saving ? <Loader2 className="animate-spin" size={14} /> : t("common.save")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </ModuleGate>
    );
}
